import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePatchRecordMutation } from '@platform/state/api';
import type { IApiErrorPlain, IRecord } from '@platform/core/types';
import { changedKeys, draftValues, normalizeValue } from './draftChanges';
import type { IRecordEditor } from './IRecordEditor';
import type { IRecordEditorOptions } from './IRecordEditorOptions';
import { useReassigner } from './useReassigner';

/**
 * Чернетка повної форми (прототип v4, editRec): запис відкривається для перегляду, «Редагувати» вмикає правку, зміни збираються
 * в чернетку й зберігаються одним PATCH («Зберегти · N» — лише змінені поля); після збереження форма повертається до перегляду.
 * Явний вибір чинного виконавця Owner/Admin простору — теж зміна (повторне призначення, рішення 1 етапу 10: PATCH {values: {assignee}}).
 * Перехід на інший запис скидає чернетку й режим. `patchNow` змінює запис одразу (If-Match поточної версії).
 */
export function useRecordEditor(ws: string, rt: string, record: IRecord | undefined, options: IRecordEditorOptions = {}): IRecordEditor {
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [tags, setTagsState] = useState<string[] | null>(null);
  const [editing, setEditing] = useState(Boolean(options.editing));
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [patch, { isLoading, error }] = usePatchRecordMutation();
  const reassigner = useReassigner(ws);
  const startEditing = Boolean(options.editing);
  useEffect(() => { setDraft({}); setTagsState(null); setEditing(startEditing); setClientErrors({}); }, [record?.id, startEditing]);

  const dirtyKeys = useMemo(() => changedKeys(record, draft, tags, reassigner), [record, draft, tags, reassigner]);
  const setField = useCallback((key: string, value: unknown) => {
    setDraft((d) => ({ ...d, [key]: normalizeValue(value) }));
    setClientErrors((e) => { if (!(key in e)) return e; const { [key]: _gone, ...rest } = e; return rest; });
  }, []);
  const setTags = useCallback((t: string[]) => setTagsState(t), []);
  const clear = useCallback(() => { setDraft({}); setTagsState(null); setClientErrors({}); }, []);
  const validate = options.validate;

  const patchNow = useCallback(async (values: Record<string, unknown>, nextTags?: string[], ext?: Record<string, unknown>) => {
    if (!record) return false;
    const body = { values: Object.fromEntries(Object.entries(values).map(([k, v]) => [k, normalizeValue(v)])), tags: nextTags, ext: ext ? { ...record.ext, ...ext } : undefined };
    try { await patch({ ws, rt, id: record.id, body, rowVersion: record.rowVersion }).unwrap(); return true; } catch { return false; }
  }, [record, patch, ws, rt]);

  const save = useCallback(async () => {
    if (!record) return false;
    if (!dirtyKeys.length) { clear(); setEditing(false); return true; }
    const invalid = validate?.(draft) ?? {};
    if (Object.keys(invalid).length) { setClientErrors(invalid); return false; }
    const ok = await patchNow(draftValues(dirtyKeys, draft), dirtyKeys.includes('tags') ? tags ?? [] : undefined);
    if (ok) { clear(); setEditing(false); }
    return ok;
  }, [record, dirtyKeys, draft, tags, patchNow, clear, validate]);

  const fieldErrors = useMemo(() => {
    const e = error as IApiErrorPlain | undefined;
    return { ...Object.fromEntries(Object.entries(e?.errors ?? {}).map(([k, v]) => [k, v.join('; ')])), ...clientErrors };
  }, [error, clientErrors]);

  return {
    draft, tags: tags ?? record?.tags ?? [], editing, dirty: dirtyKeys.length > 0, dirtyKeys, saving: isLoading, error: error as IApiErrorPlain | undefined, fieldErrors,
    setField, setTags, save, patchNow, reset: clear,
    startEdit: () => setEditing(true),
    cancelEdit: () => { clear(); setEditing(false); },
  };
}
